import { Modal, BlockStack, Text, List, Divider, Link } from "@shopify/polaris";
import { useTranslation } from "../../hooks/useTranslation";

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

export function SupportModal({ open, onClose }: SupportModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("footer.support")}
      primaryAction={{
        content: t("common.close") || "Close",
        onAction: onClose,
      }}
      size="medium"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Contact Support
          </Text>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Need help with myDATA Comply? Reach us directly:
            </Text>

            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">📞 Phone:</Text>{" "}
              <span translate="no">698&#8203;359&#8203;6733</span>
            </Text>

            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">✉️ Email:</Text>{" "}
              <span translate="no">lefteris&#8203;@&#8203;weam.gr</span>
            </Text>

            <Text as="p" variant="bodyMd" tone="subdued">
              We aim to respond within 24 hours on business days.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Common Issues & Solutions
          </Text>

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Submission Failed - Invalid Credentials
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Solution:</Text> Verify that your myDATA User ID and Subscription Key are correct in the Settings tab. Make sure you're using the correct environment (Sandbox vs Production).
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Missing Business Information
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Solution:</Text> Navigate to Settings and ensure all required fields are filled in: VAT Number (ΑΦΜ), Company Name, Address, City, and Postal Code.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Quota Exceeded
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Solution:</Text> Your monthly quota has been reached. Upgrade to a larger plan for more submissions this cycle, or wait for the next billing cycle to reset your quota.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              QR Code Not Displaying on PDF
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Solution:</Text> Ensure the delivery note was successfully submitted to myDATA. The QR code is only generated after successful submission. Check the Submissions tab for the status.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Before Contacting Support
          </Text>

          <Text as="p" variant="bodyMd">
            To help us assist you faster, please include:
          </Text>
          <List type="bullet">
            <List.Item>Your Shopify store domain (.myshopify.com)</List.Item>
            <List.Item>Order number (if applicable)</List.Item>
            <List.Item>Error message or screenshot of the issue</List.Item>
            <List.Item>When the issue occurred (date and time)</List.Item>
            <List.Item>Steps to reproduce the problem</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            Useful Resources
          </Text>

          <List type="bullet">
            <List.Item>
              <Link url="https://www.aade.gr/mydata" external>
                Official myDATA Documentation (Greek)
              </Link>
            </List.Item>
            <List.Item>
              <Link url="https://help.shopify.com" external>
                Shopify Help Center
              </Link>
            </List.Item>
            <List.Item>
              Review the Documentation section of this app for setup guides and FAQs
            </List.Item>
          </List>

          <Divider />

          <Text as="p" variant="bodySm" tone="subdued">
            We're committed to helping you achieve seamless Greek tax compliance!
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
